/**
 * Bridges theme facets.js + load-more.js to the /collections Products tab
 * (same behavior as /collections/all).
 */
(function () {
	let patched = false;
	let filterRenderGeneration = 0;
	let activeFilterGeneration = 0;
	let productsFetchAbort = null;
	let nativeFetchAbort = null;

	function isDualViewSearchPage() {
		return Boolean(
			typeof FacetFiltersForm !== 'undefined' &&
				FacetFiltersForm.isSearchPageContext() &&
				document.querySelector('.clist-all-products-toggle')
		);
	}

	function normalizeFacetParamValue(value) {
		if (window.ClistFilterOrder?.normalizeLabel) {
			return window.ClistFilterOrder.normalizeLabel(value);
		}
		return String(value)
			.trim()
			.toLowerCase()
			.replace(/[–—]/g, '-')
			.replace(/\s*&\s*/g, ' and ')
			.replace(/\s+/g, ' ');
	}

	function isAllPageNativeProductsTab() {
		if (shouldUseListProductsFetchPath()) return false;
		if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.isSearchPageContext()) {
			return false;
		}
		const path = window.location.pathname.replace(/\/+$/, '');
		if (!path.endsWith('/collections/all')) return false;
		return Boolean(window.__clistProductsViewActive);
	}

	function isCollectionProductsFacetContext() {
		if (isDualViewSearchPage()) {
			return true;
		}
		return Boolean(
			shouldUseListProductsFetchPath() ||
				document.querySelector('[data-clist-shared-toolbar]') ||
				document.querySelector('[data-clist-products-facets]') ||
				(window.location.pathname.includes('/collections/') &&
					document.getElementById('FacetFiltersForm'))
		);
	}

	function getProductsFacetsScope() {
		return document.querySelector('[data-clist-products-facets]') || document;
	}

	function dispatchSearchClistFilterSync() {
		if (!isDualViewSearchPage()) return;
		const sectionId = document.querySelector('#product-grid[data-id]')?.dataset?.id;
		if (!sectionId) return;
		document.dispatchEvent(
			new CustomEvent('clist:sync-search-clist-filters-from-url', {
				detail: { sectionId: String(sectionId) },
			})
		);
	}

	function nextFilterRenderGeneration() {
		filterRenderGeneration += 1;
		return filterRenderGeneration;
	}

	function beginFilterRenderRequest() {
		const generation = nextFilterRenderGeneration();
		activeFilterGeneration = generation;
		if (productsFetchAbort) {
			productsFetchAbort.abort();
			productsFetchAbort = null;
		}
		if (nativeFetchAbort) {
			nativeFetchAbort.abort();
			nativeFetchAbort = null;
		}
		return generation;
	}

	function isCurrentFilterGeneration(generation) {
		return generation === filterRenderGeneration;
	}

	function getActiveFilterGeneration() {
		return activeFilterGeneration;
	}

	function getListSectionRoot() {
		const cfg = window.__clistProductsConfig;
		if (cfg && cfg.sectionId) {
			return document.getElementById(`shopify-section-${cfg.sectionId}`);
		}
		return document
			.querySelector('[data-clist-shared-toolbar]')
			?.closest('[id^="shopify-section-"]');
	}

	function getProductsInner() {
		const root = getListSectionRoot();
		return root ? root.querySelector('[data-clist-products-inner]') : null;
	}

	/** /collections Products tab (fetched panel) — not /collections/all native grid. */
	function shouldUseListProductsFetchPath() {
		return Boolean(window.__clistProductsViewActive && getProductsInner());
	}

	function getProductGridContainer() {
		const root = getListSectionRoot();
		if (!root) return null;
		const inner = root.querySelector('[data-clist-products-inner]');
		if (inner) {
			const innerGrid = inner.querySelector('#ProductGridContainer');
			if (innerGrid) return innerGrid;
		}
		return root.querySelector('#ProductGridContainer');
	}

	function getProductLoadMoreGrid(root) {
		if (!root) return null;
		return root.querySelector('#product-grid') || root.querySelector('.load-more-grid');
	}

	function getProductGridChunkFromHtml(responseHTML) {
		const doc = new DOMParser().parseFromString(responseHTML, 'text/html');
		const grid = doc.querySelector('#ProductGridContainer #product-grid, #product-grid');
		if (!grid) return '';
		const items = grid.querySelectorAll(':scope > li');
		if (items.length) {
			return [...items].map((li) => li.outerHTML).join('');
		}
		const $ = window.jQuery;
		if (!$) return '';
		const parsed = $(responseHTML);
		const fromContainer = parsed.find('#ProductGridContainer #product-grid').first();
		if (fromContainer.length) return fromContainer.children('li').toArray().map((el) => el.outerHTML).join('');
		const productGrid = parsed.find('#product-grid').first();
		if (productGrid.length) return productGrid.children('li').toArray().map((el) => el.outerHTML).join('');
		return '';
	}

	function getProductsFetchPath() {
		const cfg = window.__clistProductsConfig;
		return (cfg && cfg.productsFetchPath) || '/collections/all';
	}

	const PRODUCTS_SECTION_ID_FALLBACKS = ['main-collection-product-grid', 'product-grid'];

	function getProductsSectionIdCandidates() {
		const cfg = window.__clistProductsConfig || {};
		const injectedId = getProductsInner()?.querySelector('#product-grid')?.dataset?.id;
		return [
			...new Set(
				[
					injectedId,
					cfg.productsSectionId,
					cfg.productsSectionKey,
					cfg.productsSectionType,
					...PRODUCTS_SECTION_ID_FALLBACKS,
				].filter(Boolean)
			),
		];
	}

	function buildProductsSectionUrlForId(searchParams, sectionId) {
		const path = getProductsFetchPath();
		const url = new URL(path, window.location.origin);
		const params = new URLSearchParams(searchParams || '');
		params.delete('view');
		params.delete('page');
		params.delete('phcursor');

		url.search = '';
		for (const key of params.keys()) {
			for (const val of params.getAll(key)) {
				url.searchParams.append(key, val);
			}
		}
		url.searchParams.set('section_id', sectionId);
		return url.toString();
	}

	function buildProductsSectionUrl(searchParams) {
		const candidates = getProductsSectionIdCandidates();
		return buildProductsSectionUrlForId(searchParams, candidates[0]);
	}

	function isValidProductsSectionHtml(html) {
		if (!html || typeof html !== 'string') return false;
		return (
			html.includes('ProductGridContainer') ||
			html.includes('id="product-grid"') ||
			html.includes("id='product-grid'")
		);
	}

	async function fetchProductsSectionHtml(searchParams, options = {}) {
		const cfg = window.__clistProductsConfig || {};
		const candidates = getProductsSectionIdCandidates();
		let lastStatus = 404;
		const generation =
			options.generation != null ? options.generation : beginFilterRenderRequest();

		if (productsFetchAbort) productsFetchAbort.abort();
		productsFetchAbort = new AbortController();

		for (const sectionId of candidates) {
			const url = buildProductsSectionUrlForId(searchParams, sectionId);
			try {
				const res = await fetch(url, {
					credentials: 'same-origin',
					signal: productsFetchAbort.signal,
				});
				if (!res.ok) {
					lastStatus = res.status;
					continue;
				}
				const html = await res.text();
				if (!isValidProductsSectionHtml(html)) continue;
				if (!isCurrentFilterGeneration(generation)) {
					throw new DOMException('Aborted', 'AbortError');
				}
				cfg.productsSectionId = sectionId;
				return { html, url, sectionId, generation };
			} catch (err) {
				if (err && err.name === 'AbortError') throw err;
			}
		}

		throw new Error(`HTTP ${lastStatus}`);
	}

	function collectQuickAddModalIds() {
		const modalIds = new Set();
		const pgc = getProductGridContainer();
		if (!pgc) return modalIds;
		pgc.querySelectorAll('[data-modal-open]').forEach((el) => {
			const id = el.getAttribute('data-modal-open');
			if (id) modalIds.add(id);
		});
		return modalIds;
	}

	function cleanupOrphanedQuickAddModals(options = {}) {
		const phase = options.phase === 'after' ? 'after' : 'before';
		const modalIds = collectQuickAddModalIds();

		if (phase === 'before') {
			if (modalIds.size === 0) {
				document.querySelectorAll('body > quick-add-modal[id^="QuickAdd-"]').forEach((modal) => {
					modal.remove();
				});
				return;
			}

			modalIds.forEach((id) => {
				document.querySelectorAll(`body > quick-add-modal#${CSS.escape(id)}`).forEach((modal) => {
					modal.remove();
				});
			});
			return;
		}

		document.querySelectorAll('body > quick-add-modal[id^="QuickAdd-"]').forEach((modal) => {
			if (!modal.id || !modalIds.has(modal.id)) {
				modal.remove();
			}
		});

		modalIds.forEach((id) => {
			const modals = document.querySelectorAll(`body > quick-add-modal#${CSS.escape(id)}`);
			for (let i = 0; i < modals.length - 1; i += 1) {
				modals[i].remove();
			}
		});
	}

	let lockedProductsCountReference = null;

	function parseMatchedCountFromHtml(html) {
		if (!html) return null;
		const doc = new DOMParser().parseFromString(html, 'text/html');
		const sources = [
			doc.querySelector('[data-products-count-matched]')?.getAttribute('data-products-count-matched'),
			doc.getElementById('ProductCount')?.getAttribute('data-products-count-matched'),
			doc.getElementById('ProductGridContainer')?.getAttribute('data-products-count-matched'),
			doc.getElementById('ProductGridContainer')?.getAttribute('data-filtered-products-count'),
			doc
				.querySelector('[data-clist-toolbar-part="products"]')
				?.getAttribute('data-products-count-matched'),
			doc
				.querySelector('[data-clist-toolbar-part="products"]')
				?.getAttribute('data-filtered-products-count'),
		];
		for (const raw of sources) {
			const n = parseInt(raw || '', 10);
			if (Number.isFinite(n) && n >= 0) return n;
		}
		return null;
	}

	function lockProductsCountReference(value) {
		const parsed = parseInt(value, 10);
		if (!Number.isFinite(parsed) || parsed < 0) return lockedProductsCountReference ?? 0;
		if (lockedProductsCountReference == null) lockedProductsCountReference = parsed;
		return lockedProductsCountReference;
	}

	function readProductsCountReference() {
		if (lockedProductsCountReference != null) return lockedProductsCountReference;
		const root = getListSectionRoot() || document;
		const sources = [
			root.querySelector('[data-products-count-meta]')?.getAttribute('data-products-count-reference'),
			root
				.querySelector('[data-clist-toolbar-part="products"]')
				?.getAttribute('data-products-count-reference'),
			getProductGridContainer()?.getAttribute('data-products-count-reference'),
			getProductGridContainer()?.getAttribute('data-all-products-count'),
			document.getElementById('ProductCount')?.getAttribute('data-products-count-reference'),
			document.getElementById('ProductCountDesktop')?.getAttribute('data-products-count-reference'),
		];
		for (const raw of sources) {
			const n = parseInt(raw || '', 10);
			if (Number.isFinite(n) && n >= 0) return lockProductsCountReference(n);
		}
		return 0;
	}

	function readProductsCountMatched() {
		const root = getListSectionRoot() || document;
		const sources = [
			root.querySelector('[data-products-count-meta]')?.getAttribute('data-products-count-matched'),
			root
				.querySelector('[data-clist-toolbar-part="products"]')
				?.getAttribute('data-products-count-matched'),
			getProductGridContainer()?.getAttribute('data-products-count-matched'),
			getProductGridContainer()?.getAttribute('data-filtered-products-count'),
			document.getElementById('ProductCount')?.getAttribute('data-products-count-matched'),
			document
				.querySelector('[data-clist-shared-toolbar] .clist-toolbar-count [data-clist-toolbar-part="products"]')
				?.getAttribute('data-products-count-matched'),
			document.getElementById('ProductCountDesktop')?.getAttribute('data-products-count-matched'),
		];
		for (const raw of sources) {
			const n = parseInt(raw || '', 10);
			if (Number.isFinite(n) && n >= 0) return n;
		}
		return readProductsCountReference();
	}

	function setProductsCountMatched(value) {
		const matched = Math.max(0, parseInt(value, 10) || 0);
		const root = getListSectionRoot() || document;
		root.querySelectorAll('[data-products-count-matched]').forEach((el) => {
			el.setAttribute('data-products-count-matched', String(matched));
		});
		const pgc = getProductGridContainer();
		if (pgc) {
			pgc.setAttribute('data-products-count-matched', String(matched));
			pgc.setAttribute('data-filtered-products-count', String(matched));
		}
		const productCount = document.getElementById('ProductCount');
		if (productCount) productCount.setAttribute('data-products-count-matched', String(matched));
	}

	function writeProductsToolbarCount(matched, reference) {
		const safeReference = Math.max(0, reference);
		let safeMatched = Math.max(0, matched);
		if (safeReference > 0) safeMatched = Math.min(safeMatched, safeReference);
		const text = `Showing ${safeMatched} of ${safeReference} Products`;
		const root = getListSectionRoot() || document;
		root.querySelectorAll(
			'[data-clist-shared-toolbar] .clist-toolbar-count [data-clist-toolbar-part="products"], .facets__product-count-text #ProductCountDesktop, #ProductCount'
		).forEach((el) => {
			el.textContent = text;
		});
	}

	function isSearchProductsFuzzyCountContext() {
		if (typeof FacetFiltersForm === 'undefined' || !FacetFiltersForm.isSearchPageContext()) {
			return false;
		}
		const root = getListSectionRoot();
		if (root?.classList.contains('clist-view-collections-active')) return false;
		if (window.__clistProductsViewActive === false) return false;
		return true;
	}

	function renderProductsToolbarCount() {
		if (!isProductsCountContext()) return;
		const reference = readProductsCountReference();
		let matched = readProductsCountMatched();
		if (reference > 0) matched = Math.min(matched, reference);
		writeProductsToolbarCount(matched, reference);
	}

	function applyAuthoritativeCountFromHtml(html, generation) {
		if (generation != null && !isCurrentFilterGeneration(generation)) return;
		if (isSearchProductsFuzzyCountContext()) return;
		if (html) {
			const parsed = parseMatchedCountFromHtml(html);
			if (parsed != null) setProductsCountMatched(parsed);
		}
		renderProductsToolbarCount();
	}

	function applyProductGridTotalFromHtml(html) {
		const parsed = parseMatchedCountFromHtml(html);
		if (parsed != null) setProductsCountMatched(parsed);
	}

	function updateToolbarProductCount(html, generation) {
		applyAuthoritativeCountFromHtml(html, generation);
	}

	function syncProductsToolbarCountFromDom(html, generation) {
		if (html) {
			applyAuthoritativeCountFromHtml(html, generation);
			return;
		}
		if (generation != null && !isCurrentFilterGeneration(generation)) return;
		renderProductsToolbarCount();
	}

	function hasSharedProductsToolbar() {
		return Boolean(
			document.querySelector('[data-clist-shared-toolbar] [data-clist-toolbar-part="products"]')
		);
	}

	function isProductsCountContext() {
		return Boolean(
			window.__clistProductsViewActive ||
				document.querySelector('.clist-view-products-active') ||
				document.querySelector('#view-products-content:not([hidden])') ||
				document.querySelector('[data-clist-shared-toolbar]') ||
				document.getElementById('ProductCountDesktop') ||
				document.getElementById('ProductCount') ||
				getProductGridContainer()?.hasAttribute('data-products-count-reference')
		);
	}

	function wireProductsLoadMore() {
		const inner = getProductsInner();
		if (!inner || typeof window.jQuery === 'undefined') return;

		const $ = window.jQuery;
		const grid = getProductLoadMoreGrid(getProductGridContainer() || inner);
		if (!grid) return;

		const normalizeNextUrl = (value) => {
			if (!value) return '';
			const base = getProductsFetchPath();
			const url = new URL(value, window.location.origin);
			if (!url.pathname.includes('/collections/')) {
				const baseUrl = new URL(base, window.location.origin);
				url.pathname = baseUrl.pathname;
			}
			const sectionId = getProductsSectionIdCandidates()[0];
			if (sectionId) {
				url.searchParams.set('section_id', sectionId);
			}
			url.searchParams.delete('phcursor');
			url.searchParams.delete('view');
			return url.pathname + url.search;
		};

		const getLoadMoreStateScope = (pgc, buttonEl) => {
			const wrapper = buttonEl?.closest('.button-wrapper');
			const scopeFromWrapper = wrapper?.parentElement;
			if (scopeFromWrapper && scopeFromWrapper.querySelector('#product-grid')) return scopeFromWrapper;
			return pgc || inner;
		};

		const readScopeMeta = (scope) => {
			if (!scope) return null;
			const totalPages = parseInt(scope.querySelector('[data-total-pages]')?.value || '', 10);
			const currentPage = parseInt(scope.querySelector('[data-current-page]')?.value || '', 10);
			const nextUrlRaw = scope.querySelector('[data-next-url]')?.value || '';
			if (!Number.isFinite(totalPages) && !Number.isFinite(currentPage) && !nextUrlRaw) return null;
			return {
				totalPages: Number.isFinite(totalPages) ? totalPages : null,
				currentPage: Number.isFinite(currentPage) ? currentPage : null,
				nextUrl: normalizeNextUrl(nextUrlRaw),
			};
		};

		const buildFallbackNextUrl = (scopeMeta) => {
			if (!scopeMeta || !scopeMeta.currentPage || !scopeMeta.totalPages) return '';
			if (scopeMeta.currentPage >= scopeMeta.totalPages) return '';
			const params = getCurrentFacetSearchParams();
			const base = new URL(buildProductsSectionUrl(params.toString()), window.location.origin);
			base.searchParams.set('page', String(scopeMeta.currentPage + 1));
			base.searchParams.delete('phcursor');
			base.searchParams.delete('view');
			return base.pathname + base.search;
		};

		const syncPaginationFromResponse = (responseHTML, buttonEl) => {
			const parsed = new DOMParser().parseFromString(responseHTML || '', 'text/html');
			const fresh = parsed.getElementById('ProductGridContainer');
			if (!fresh) return null;

			const freshScope =
				[...fresh.querySelectorAll('.collection-product:not(.collection--empty)')].find((node) =>
					Boolean(node.querySelector('#product-grid'))
				) || fresh;
			const freshMeta = readScopeMeta(freshScope) || readScopeMeta(fresh);
			if (!freshMeta) return null;

			const pgc = getProductGridContainer();
			const targetScope = getLoadMoreStateScope(pgc, buttonEl);
			const totalPagesInput = targetScope?.querySelector('[data-total-pages]');
			const currentPageInput = targetScope?.querySelector('[data-current-page]');
			const nextUrlInput = targetScope?.querySelector('[data-next-url]');

			if (totalPagesInput && freshMeta?.totalPages && freshMeta.totalPages > 0) {
				totalPagesInput.value = String(freshMeta.totalPages);
			}
			if (currentPageInput && freshMeta?.currentPage && freshMeta.currentPage > 0) {
				currentPageInput.value = String(freshMeta.currentPage);
			}
			if (nextUrlInput) {
				nextUrlInput.value = freshMeta?.nextUrl || '';
				nextUrlInput.dataset.nextUrl = freshMeta?.nextUrl || '';
			}

			if (freshMeta.nextUrl) return { hasNext: true };
			if (
				Number.isFinite(freshMeta.totalPages) &&
				Number.isFinite(freshMeta.currentPage) &&
				freshMeta.totalPages > 0
			) {
				return { hasNext: freshMeta.currentPage < freshMeta.totalPages };
			}
			return null;
		};

		inner.querySelectorAll('#ProductGridContainer .js-load-more').forEach((button) => {
			button.onclick = () => {
				const pgc = getProductGridContainer();
				const stateScope = getLoadMoreStateScope(pgc, button);
				const scopeMeta = readScopeMeta(stateScope);
				const nextUrl = scopeMeta?.nextUrl || buildFallbackNextUrl(scopeMeta);
				if (!nextUrl) return;

				button.setAttribute('disabled', '');
				button.classList.add('loading');
				let paginationState = null;

				$.ajax({
					url: nextUrl,
					type: 'GET',
					dataType: 'html',
					success(responseHTML) {
						const scrollTop = $(window).scrollTop();
						const chunk = getProductGridChunkFromHtml(responseHTML);
						if (chunk) $(grid).append(chunk);
						if (typeof colorSwatches === 'function') colorSwatches();
						$(window).scrollTop(scrollTop);
						syncProductsToolbarCountFromDom();
						paginationState = syncPaginationFromResponse(responseHTML, button);
						const scope = inner.closest('[id^="shopify-section-"]');
						window.ClistMobileGridBorders?.scheduleUpdate?.(scope || document);
					},
					complete() {
						button.removeAttribute('disabled');
						button.classList.remove('loading');
						const scope = inner.closest('[id^="shopify-section-"]');
						window.ClistMobileGridBorders?.scheduleUpdate?.(scope || document);
						if (paginationState && !paginationState.hasNext) {
							button.remove();
						}
					},
				});
			};
		});
	}

	function captureNativeFacetCheckboxState() {
		syncNativeFacetCheckboxesFromSearchParams();
		const checkedByParam = new Map();
		const forms = [document.getElementById('FacetFiltersForm'), document.getElementById('FacetFiltersFormMobile')].filter(
			Boolean
		);
		for (const form of forms) {
			form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
				if (!input.name) return;
				if (!checkedByParam.has(input.name)) checkedByParam.set(input.name, new Map());
				const li = input.closest('li');
				checkedByParam.get(input.name).set(input.value, li ? li.cloneNode(true) : null);
			});
		}
		return checkedByParam;
	}

	function mergeNativeFacetFilterGroup(targetFilter, freshElement, checkedByParam) {
		if (!targetFilter || !freshElement) return;

		const param = targetFilter.dataset.filterParam || freshElement.dataset.filterParam;
		const checkedForParam = param ? checkedByParam.get(param) : null;

		// Replace with fresh Shopify markup only. Do NOT re-append prior <li> clones — they
		// keep stale Filter-{group}-{index} ids that collide after Shopify re-indexes forloop
		// on the next response (label[for] then targets the wrong input; hover/click hit wrong row).
		targetFilter.innerHTML = freshElement.innerHTML;

		const list = targetFilter.querySelector('.facets__list, .mobile-facets__list, ul[role="list"], ul');
		if (!list) return;

		list.querySelectorAll('input[type="checkbox"]').forEach((input) => {
			const normValue = normalizeFacetParamValue(input.value);
			const isSelected = Boolean(
				checkedForParam &&
					[...checkedForParam.keys()].some(
						(value) => normalizeFacetParamValue(value) === normValue
					)
			);

			if (isSelected) input.checked = true;
			input.disabled = false;

			const label =
				input.nextElementSibling?.tagName === 'LABEL'
					? input.nextElementSibling
					: input.closest('li')?.querySelector('label');
			label?.classList.remove('facet-checkbox--disabled', 'mobile-facets__label--disabled');
			input.closest('li')?.classList.remove('facets__item--zero-count');
		});
	}

	function applyProductsFacetHtmlMerge(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
		const checkedByParam = captureNativeFacetCheckboxState();
		const facetDetailsElements = parsedHTML.querySelectorAll(
			'#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
		);
		const matchesIndex = (element) => {
			const jsFilter = event ? event.target.closest('.js-filter') : undefined;
			return jsFilter ? element.dataset.filterParam === jsFilter.dataset.filterParam : false;
		};
		const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
		const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

		facetsToRender.forEach((element) => {
			const param = element.dataset.filterParam;
			if (!param) return;
			const isMobile = Boolean(element.closest('#FacetFiltersFormMobile'));
			const scope = isMobile ? '#FacetFiltersFormMobile' : '#FacetFiltersForm';
			const targetFilter =
				document.querySelector(`${scope} .js-filter[data-filter-param="${CSS.escape(param)}"]`) ||
				[...document.querySelectorAll(`${scope} .js-filter`)].find((group) =>
					group.querySelector(`input[type="checkbox"][name="${CSS.escape(param)}"]`)
				);
			if (targetFilter) {
				if (!targetFilter.getAttribute('data-filter-param')) {
					targetFilter.setAttribute('data-filter-param', param);
				}
				mergeNativeFacetFilterGroup(targetFilter, element, checkedByParam);
			}
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
		FacetFiltersForm.renderAdditionalElements(parsedHTML);
		document.querySelector('facet-filters-form')?.bindFacetTriggers();
		if (countsToRender && event?.target) {
			FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
		}
	}

	function getCurrentFacetSearchParams() {
		if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.searchParamsPrev != null) {
			return new URLSearchParams(FacetFiltersForm.searchParamsPrev || '');
		}
		return new URLSearchParams(window.location.search);
	}

	function syncNativeFacetCheckboxesFromSearchParams() {
		if (typeof FacetFiltersForm === 'undefined' || FacetFiltersForm.searchParamsPrev == null) return;
		const params = new URLSearchParams(FacetFiltersForm.searchParamsPrev || '');
		const forms = [
			document.getElementById('FacetFiltersForm'),
			document.getElementById('FacetFiltersFormMobile'),
		].filter(Boolean);
		for (const form of forms) {
			form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
				input.checked = params.getAll(input.name).some(
					(urlVal) => normalizeFacetParamValue(urlVal) === normalizeFacetParamValue(input.value)
				);
			});
		}
	}

	function buildRemoveOneFilterHref(param, value, baseParams) {
		const url = new URL(window.location.href);
		const params = new URLSearchParams(baseParams || '');
		const remaining = params.getAll(param).filter((v) => v !== value);
		params.delete(param);
		remaining.forEach((v) => params.append(param, v));
		url.search = params.toString() ? `?${params.toString()}` : '';
		return `${url.pathname}${url.search}${url.hash}`;
	}

	function getActiveFacetCloseIconMarkup() {
		const source = document.querySelector('.active-facets__button-close');
		if (source && source.innerHTML) return source.innerHTML;
		// return '&times;';
		return '<svg width="19" height="19" viewBox="0 0 19 19" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.5625 4.4375L4.4375 14.5625" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M14.5625 14.5625L4.4375 4.4375" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
	}

	function encodeFacetAttr(value) {
		return encodeURIComponent(String(value == null ? '' : value));
	}

	function findNativeFacetInput(param, value) {
		const forms = [document.getElementById('FacetFiltersForm'), document.getElementById('FacetFiltersFormMobile')].filter(
			Boolean
		);
		for (const form of forms) {
			const input = form.querySelector(
				`input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(value)}"]`
			);
			if (input) return input;
		}
		return null;
	}

	function findNativeFacetInputInForm(form, param, value) {
		if (!form) return null;
		const exact = form.querySelector(
			`input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(String(value))}"]`
		);
		if (exact) return exact;
		const norm = normalizeFacetParamValue(value);
		for (const input of form.querySelectorAll(
			`input[type="checkbox"][name="${CSS.escape(param)}"]`
		)) {
			if (normalizeFacetParamValue(input.value) === norm) return input;
		}
		return null;
	}

	function getFilterOrderCatalog() {
		const shortKeys = window.ClistFilterOrder?.FILTER_SHORT_KEYS_ORDER;
		if (shortKeys?.length) {
			const orders =
				window.ClistFilterOrder?.FILTER_ORDER || window.ClistFilterOrder?.FILTER_VALUE_ORDERS || {};
			return shortKeys.map(({ key, label, storefrontParam }) => ({
				groupKey: key,
				label,
				param: storefrontParam,
				values: orders[key] || [],
			}));
		}

		const orders =
			window.ClistFilterOrder?.FILTER_ORDER || window.ClistFilterOrder?.FILTER_VALUE_ORDERS;
		const paramToLabel = window.ClistFilterOrder?.STOREFRONT_PARAM_TO_LABEL || {};
		if (!orders) return [];

		return Object.entries(orders).map(([groupKey, values]) => {
			let param = getStorefrontParamForGroupKey(groupKey);
			let label = groupKey;
			for (const [candidateParam, candidateLabel] of Object.entries(paramToLabel)) {
				if (candidateParam.endsWith(groupKey)) {
					param = candidateParam;
					label = candidateLabel;
					break;
				}
			}
			return { groupKey, label, param, values };
		});
	}

	function findProductsFilterGroup(root, formId, param, groupLabel) {
		let group = root.querySelector(`${formId} .js-filter[data-filter-param="${CSS.escape(param)}"]`);
		if (group) return group;

		for (const candidate of root.querySelectorAll(`${formId} .js-filter`)) {
			if (candidate.querySelector(`input[type="checkbox"][name="${CSS.escape(param)}"]`)) {
				candidate.setAttribute('data-filter-param', param);
				return candidate;
			}
		}

		const labelNorm = normalizeFacetParamValue(groupLabel);
		for (const candidate of root.querySelectorAll(`${formId} .js-filter`)) {
			const candidateLabel = normalizeFacetParamValue(getProductsFilterGroupLabel(candidate, ''));
			if (candidateLabel && candidateLabel === labelNorm) {
				candidate.setAttribute('data-filter-param', param);
				return candidate;
			}
		}

		return null;
	}

	function setFilterGroupSummaryLabel(filterGroup, groupLabel, mobile) {
		if (!filterGroup || !groupLabel) return;
		if (mobile) {
			const mobileSummary = filterGroup.querySelector('.mobile-facets__summary > div > span');
			if (mobileSummary) mobileSummary.textContent = groupLabel;
			const closeBtn = filterGroup.querySelector('.mobile-facets__close-button');
			if (closeBtn) {
				const arrow = closeBtn.querySelector('.mobile-facets__arrow, svg, .icon');
				closeBtn.textContent = '';
				if (arrow) closeBtn.append(arrow);
				closeBtn.append(document.createTextNode(groupLabel));
			}
			return;
		}
		const desktopTitle = filterGroup.querySelector('.facets__summary-title');
		if (desktopTitle) {
			const arrow = desktopTitle.querySelector('.facets__arrow, svg, .icon');
			desktopTitle.textContent = '';
			desktopTitle.append(document.createTextNode(groupLabel));
			if (arrow) desktopTitle.append(arrow);
		}
	}

	function ensureProductsFilterGroupShell(root, formId, param, groupLabel, mobile) {
		const existing = findProductsFilterGroup(root, formId, param, groupLabel);
		if (existing) return existing;

		const wrapper = root.querySelector(
			`${formId} #FacetsWrapperDesktop, ${formId} .facets__wrapper, ${formId} .mobile-facets__main`
		);
		if (!wrapper) return null;

		const template = wrapper.querySelector('.js-filter');
		if (!template) return null;

		const clone = template.cloneNode(true);
		clone.setAttribute('data-filter-param', param);
		clone.classList.add('clist-injected-facet-group');
		if (mobile) {
			clone.classList.add('mobile-facets__details', 'js-filter');
		} else {
			clone.classList.add('facets__disclosure', 'js-filter');
		}
		clone.open = false;

		const list = getProductsFacetList(clone);
		if (list) list.innerHTML = '';

		setFilterGroupSummaryLabel(clone, groupLabel, mobile);
		wrapper.appendChild(clone);
		return clone;
	}

	function findNativeFacetInputByNormalizedValue(param, value, form) {
		if (form) {
			return findNativeFacetInputInForm(form, param, value);
		}
		const exact = findNativeFacetInput(param, value);
		if (exact) return exact;
		const norm = normalizeFacetParamValue(value);
		const forms = [
			document.getElementById('FacetFiltersForm'),
			document.getElementById('FacetFiltersFormMobile'),
		].filter(Boolean);
		for (const candidateForm of forms) {
			const input = findNativeFacetInputInForm(candidateForm, param, value);
			if (input && normalizeFacetParamValue(input.value) === norm) return input;
		}
		return null;
	}

	function resolveGroupKeyFromStorefrontParam(param) {
		const shortKeys = window.ClistFilterOrder?.FILTER_SHORT_KEYS_ORDER;
		if (shortKeys?.length) {
			const found = shortKeys.find((entry) => entry.storefrontParam === param);
			if (found) return found.key;
		}
		const match = String(param).match(/^filter\.(?:p|v)\.m\.(.+)$/);
		return match ? match[1] : '';
	}

	function getCanonicalFilterOrderValue(groupKey, rawValue) {
		if (window.ClistFilterOrder?.canonicalFilterLabelForKey) {
			return window.ClistFilterOrder.canonicalFilterLabelForKey(groupKey, rawValue);
		}
		const orders =
			window.ClistFilterOrder?.FILTER_ORDER || window.ClistFilterOrder?.FILTER_VALUE_ORDERS || {};
		const order = orders[groupKey];
		if (!order) return String(rawValue).trim();
		const norm = normalizeFacetParamValue(rawValue);
		const found = order.find((candidate) => normalizeFacetParamValue(candidate) === norm);
		return found || String(rawValue).trim();
	}

	function getCatalogStorefrontParam(groupKey) {
		const shortKeys = window.ClistFilterOrder?.FILTER_SHORT_KEYS_ORDER;
		if (shortKeys?.length) {
			const entry = shortKeys.find((candidate) => candidate.key === groupKey);
			if (entry?.storefrontParam) return entry.storefrontParam;
		}
		return getStorefrontParamForGroupKey(groupKey);
	}

	function getCatalogFacetFieldRef(groupKey, rawValue) {
		const param = getCatalogStorefrontParam(groupKey);
		const value = getCanonicalFilterOrderValue(groupKey, rawValue);
		return { groupKey, param, name: param, value };
	}

	function findFacetCheckboxInList(list, param, value) {
		if (!list || !param) return null;
		const norm = normalizeFacetParamValue(value);
		for (const input of list.querySelectorAll(
			`input[type="checkbox"][name="${CSS.escape(param)}"]`
		)) {
			if (normalizeFacetParamValue(input.value) === norm) return input;
		}
		return null;
	}

	function removeInjectedProductsFacetCheckboxes(root) {
		root.querySelectorAll('.clist-injected-facet-order-value, .clist-injected-facet-value').forEach((li) => {
			li.remove();
		});
	}

	function buildProductsFacetInputId(param, value, mobile) {
		const sectionId = document.getElementById('product-grid')?.dataset?.id || '0';
		let suffix = 0;
		let id = `Filter-clist-${safeFacetDomId(param)}-${safeFacetDomId(value)}-${
			mobile ? 'mobile' : 'desktop'
		}-${sectionId}`;
		while (document.getElementById(id)) {
			suffix += 1;
			id = `Filter-clist-${safeFacetDomId(param)}-${safeFacetDomId(value)}-${
				mobile ? 'mobile' : 'desktop'
			}-${sectionId}-${suffix}`;
		}
		return id;
	}

	function buildProductsFacetCheckboxLi({ fieldRef, checked, inputId, mobile }) {
		const { name, value } = fieldRef;
		const li = document.createElement('li');
		li.className = mobile
			? 'mobile-facets__item list-menu__item clist-injected-facet-order-value'
			: 'list-menu__item facets__item clist-injected-facet-order-value';

		const input = document.createElement('input');
		input.type = 'checkbox';
		input.className = mobile ? 'mobile-facets__checkbox' : 'focus-inset';
		input.name = name;
		input.value = value;
		input.id = inputId;
		input.checked = checked;
		input.disabled = false;

		const label = document.createElement('label');
		label.htmlFor = inputId;
		label.className = mobile ? 'mobile-facets__label facet-checkbox' : 'facet-checkbox';
		const span = document.createElement('span');
		span.className = 'facet-checkbox__label small-font';
		span.textContent = value;
		label.appendChild(span);
		li.appendChild(input);
		li.appendChild(label);
		return li;
	}

	const PRODUCTS_VARIANT_FILTER_GROUP_KEYS = new Set([
		'plytix.size_small_filter',
		'plytix.size_large_filter',
		'plytix.size_x_large_filter',
		'plytix.wall_tile_specifics_filter',
		'plytix.mosaic_specifics_filter',
		'plytix.trim_specifics_filter',
	]);

	function getStorefrontParamForGroupKey(groupKey) {
		const prefix = PRODUCTS_VARIANT_FILTER_GROUP_KEYS.has(groupKey) ? 'filter.v.m.' : 'filter.p.m.';
		return `${prefix}${groupKey}`;
	}

	function safeFacetDomId(raw) {
		return String(raw).replace(/[^a-zA-Z0-9_-]/g, '_');
	}

	function isParamValueSelected(params, param, value) {
		const norm = normalizeFacetParamValue(value);
		return params.getAll(param).some((candidate) => normalizeFacetParamValue(candidate) === norm);
	}

	function getProductsFacetList(filterGroup) {
		return filterGroup?.querySelector('.facets__list, .mobile-facets__list, ul[role="list"], ul');
	}

	function getProductsFilterGroupLabel(filterGroup, groupKey) {
		return (
			filterGroup
				?.querySelector('.facets__summary-title, .mobile-facets__summary > div > span, .mobile-facets__summary span')
				?.textContent?.replace(/\s+/g, ' ')
				.trim() || groupKey
		);
	}

	function sortProductsFacetListItems(list, groupKey, groupLabel) {
		if (!list || !window.ClistFilterOrder?.compareValues) return;
		const items = [...list.querySelectorAll(':scope > li')];
		if (items.length < 2) return;
		items.sort((a, b) => {
			const aLabel = a.querySelector('.facet-checkbox__label')?.textContent?.trim() || '';
			const bLabel = b.querySelector('.facet-checkbox__label')?.textContent?.trim() || '';
			return window.ClistFilterOrder.compareValues(groupKey, groupLabel, aLabel, bLabel);
		});
		items.forEach((item) => list.appendChild(item));
	}

	function normalizeProductsFacetCheckboxPairings(root) {
		const scope = root && root !== document ? root : document;

		for (const formSelector of ['#FacetFiltersForm', '#FacetFiltersFormMobile']) {
			const form = scope.querySelector(formSelector);
			if (!form) continue;
			const mobile = formSelector === '#FacetFiltersFormMobile';

			form.querySelectorAll('.js-filter').forEach((filterGroup) => {
				const list = getProductsFacetList(filterGroup);
				if (!list) return;

				list.querySelectorAll(':scope > li').forEach((li) => {
					if (li.classList.contains('clist-injected-facet-value') && li.hidden) return;

					const input = li.querySelector(':scope > input[type="checkbox"]');
					const label = li.querySelector(':scope > label');
					if (!input || !label || !input.name) return;

					const value = input.value;
					const inputId = buildProductsFacetInputId(input.name, value, mobile);
					input.id = inputId;
					label.htmlFor = inputId;

					const labelSpan = label.querySelector('.facet-checkbox__label');
					if (labelSpan) labelSpan.textContent = value;
				});
			});
		}
	}

	function rebuildProductsSidebarFilterCheckboxes(params) {
		if (!isCollectionProductsFacetContext()) return;

		const catalog = getFilterOrderCatalog();
		if (!catalog.length) return;

		const searchParams =
			params instanceof URLSearchParams ? params : new URLSearchParams(params || '');

		const scope = getProductsFacetsScope();
		const root = scope === document ? document : scope;

		removeInjectedProductsFacetCheckboxes(root);

		for (const mobile of [false, true]) {
			const formId = mobile ? '#FacetFiltersFormMobile' : '#FacetFiltersForm';
			const formEl = document.querySelector(formId);
			if (!formEl) continue;

			for (const { groupKey, label, param, values } of catalog) {
				if (!param || !Array.isArray(values) || values.length === 0) continue;

				let filterGroup = findProductsFilterGroup(root, formId, param, label);
				if (!filterGroup) {
					filterGroup = ensureProductsFilterGroupShell(root, formId, param, label, mobile);
				}
				if (!filterGroup) continue;

				filterGroup.setAttribute('data-filter-param', param);

				const list = getProductsFacetList(filterGroup);
				if (!list) continue;

				const groupLabel = getProductsFilterGroupLabel(filterGroup, groupKey);

				for (const catalogValue of values) {
					const fieldRef = getCatalogFacetFieldRef(groupKey, catalogValue);
					if (!fieldRef.value) continue;
					if (findFacetCheckboxInList(list, param, fieldRef.value)) continue;

					const checked = isParamValueSelected(searchParams, param, fieldRef.value);
					const inputId = buildProductsFacetInputId(param, fieldRef.value, mobile);
					const li = buildProductsFacetCheckboxLi({
						fieldRef: { name: param, value: fieldRef.value },
						checked,
						inputId,
						mobile,
					});
					list.appendChild(li);
				}

				sortProductsFacetListItems(list, groupKey, groupLabel);
			}
		}

		const seenNorm = new Set();
		for (const urlParam of searchParams.keys()) {
			if (!urlParam.startsWith('filter.')) continue;
			const groupKey = resolveGroupKeyFromStorefrontParam(urlParam);
			const param = groupKey ? getCatalogStorefrontParam(groupKey) : urlParam;

			for (const urlValue of searchParams.getAll(urlParam)) {
				const trimmed = String(urlValue).trim();
				if (!trimmed) continue;
				const fieldRef = groupKey
					? getCatalogFacetFieldRef(groupKey, trimmed)
					: { name: param, value: trimmed };
				const normToken = `${fieldRef.name}\0${normalizeFacetParamValue(fieldRef.value)}`;
				if (seenNorm.has(normToken)) continue;
				seenNorm.add(normToken);

				if (findNativeFacetInput(fieldRef.name, fieldRef.value)) continue;

				for (const mobile of [false, true]) {
					const formId = mobile ? '#FacetFiltersFormMobile' : '#FacetFiltersForm';
					const catalogEntry = catalog.find((entry) => entry.param === param);
					const filterGroup = findProductsFilterGroup(
						root,
						formId,
						param,
						catalogEntry?.label || ''
					);
					const list = getProductsFacetList(filterGroup);
					if (!list) continue;
					if (findFacetCheckboxInList(list, fieldRef.name, fieldRef.value)) continue;

					const li = document.createElement('li');
					li.className = mobile
						? 'mobile-facets__item list-menu__item clist-injected-facet-value'
						: 'list-menu__item facets__item clist-injected-facet-value';
					li.hidden = true;
					const input = document.createElement('input');
					input.type = 'checkbox';
					input.name = fieldRef.name;
					input.value = fieldRef.value;
					input.checked = true;
					input.className = 'clist-injected-facet-input';
					li.appendChild(input);
					list.appendChild(li);
				}
			}
		}

		normalizeProductsFacetCheckboxPairings(root);
	}

	function ensureAllProductsFilterOrderValuesVisible(params) {
		rebuildProductsSidebarFilterCheckboxes(params);
	}

	function enableAllProductsFiltersCheckboxes() {
		const scope = getProductsFacetsScope();
		const root = scope === document ? document : scope;
		root.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
			input.disabled = false;
			const li = input.closest('li');
			const label = input.closest('label');
			li?.classList.remove('facets__item--zero-count');
			label?.classList.remove('facet-checkbox--disabled');
			if (li?.classList.contains('clist-injected-facet-value') && input.checked) {
				li.hidden = false;
			}
		});
	}

	function syncProductsSidebarFilterOptions(params) {
		if (!isCollectionProductsFacetContext()) return;
		const searchParams =
			params instanceof URLSearchParams
				? params
				: new URLSearchParams(params || getCurrentFacetSearchParams() || window.location.search);
		rebuildProductsSidebarFilterCheckboxes(searchParams);
		enableAllProductsFiltersCheckboxes();
		if (window.ClistFilterOrder?.reorderProductFacetsInDom) {
			window.ClistFilterOrder.reorderProductFacetsInDom(getProductsFacetsScope());
		}
		if (window.ClistFilterOrder?.applyCanonicalGroupLabelsInDom) {
			window.ClistFilterOrder.applyCanonicalGroupLabelsInDom(getProductsFacetsScope());
		}
		rebuildNativeActiveFacetsFromForm(searchParams);
	}

	function scheduleSyncProductsSidebarFilterOptions(params) {
		const searchParams =
			params instanceof URLSearchParams
				? params
				: params != null
					? new URLSearchParams(params)
					: null;
		requestAnimationFrame(() => {
			syncProductsSidebarFilterOptions(searchParams || getCurrentFacetSearchParams());
		});
	}

	function ensureSelectedProductsFiltersVisible(params) {
		rebuildProductsSidebarFilterCheckboxes(params);
	}

	function getNativeFacetSelection(params) {
		const seen = new Set();
		const selections = [];
		const addSelection = (param, value) => {
			const normToken = `${param}\0${normalizeFacetParamValue(value)}`;
			if (seen.has(normToken)) return;
			seen.add(normToken);

			const inputEl = findNativeFacetInputByNormalizedValue(param, value);
			const filterGroup = inputEl?.closest('.js-filter');
			const filterLabel =
				filterGroup?.querySelector('.facets__summary-title')?.textContent?.replace(/\s+/g, ' ').trim() ||
				param;
			const valueLabel =
				inputEl?.closest('label')?.querySelector('.facet-checkbox__label')?.textContent?.trim() ||
				inputEl?.value ||
				value;

			selections.push({ param, value, filterLabel, valueLabel });
		};

		const searchParams = params instanceof URLSearchParams ? params : new URLSearchParams(params || '');
		for (const key of searchParams.keys()) {
			if (!key.startsWith('filter.')) continue;
			for (const value of searchParams.getAll(key)) {
				const trimmed = String(value || '').trim();
				if (!trimmed) continue;
				addSelection(key, trimmed);
			}
		}

		return selections;
	}

	function buildClearAllFacetHref(baseParams) {
		const url = new URL(window.location.href);
		const params = new URLSearchParams(baseParams || '');
		for (const key of [...params.keys()]) {
			if (key.startsWith('filter.')) params.delete(key);
		}
		url.search = params.toString() ? `?${params.toString()}` : '';
		return `${url.pathname}${url.search}${url.hash}`;
	}

	function uncheckNativeFacetValue(param, value) {
		if (!param || value == null) return;
		const forms = [
			document.getElementById('FacetFiltersForm'),
			document.getElementById('FacetFiltersFormMobile'),
		].filter(Boolean);
		for (const form of forms) {
			const input = form.querySelector(
				`input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(String(value))}"]`
			);
			if (input) input.checked = false;
		}
	}

	function clearNativeFacetCheckboxes() {
		const forms = [
			document.getElementById('FacetFiltersForm'),
			document.getElementById('FacetFiltersFormMobile'),
		].filter(Boolean);
		for (const form of forms) {
			form.querySelectorAll('input[type="checkbox"][name^="filter."]:checked').forEach((input) => {
				input.checked = false;
			});
		}
	}

	function rebuildNativeActiveFacetsFromForm(params) {
		if (!isCollectionProductsFacetContext() || shouldUseListProductsFetchPath()) return;

		const stateParams =
			params instanceof URLSearchParams
				? params
				: params != null
					? new URLSearchParams(params)
					: getCurrentFacetSearchParams();
		const selections = getNativeFacetSelection(stateParams);
		const scope = getProductsFacetsScope();
		const desktopWrapper = scope.querySelector('.active-facets-desktop .active-facets__wrapper');
		const mobileContainer = scope.querySelector('.active-facets-mobile');
		const closeIconMarkup = getActiveFacetCloseIconMarkup();

		const buildPillsHtml = () => {
			let pillsHtml = '';
			for (const sel of selections) {
				const removeHref = buildRemoveOneFilterHref(sel.param, sel.value, stateParams);
				pillsHtml += `
<facet-remove>
	<a
		href="${removeHref}"
		class="active-facets__button focus-inset small-font clist-active-facet-pill"
		data-filter-param="${encodeFacetAttr(sel.param)}"
		data-filter-value="${encodeFacetAttr(sel.value)}"
	>
		<span class="active-facets__button-inner">
			<span>${sel.filterLabel}:</span> ${sel.valueLabel}
			<span class="active-facets__button-close">${closeIconMarkup}</span>
		</span>
		<span class="visually-hidden">Clear filter</span>
	</a>
</facet-remove>`;
			}
			return pillsHtml;
		};

		if (selections.length === 0) {
			if (desktopWrapper) {
				desktopWrapper.innerHTML = '';
				desktopWrapper.classList.remove('active-facets__wrapper_margin');
			}
			if (mobileContainer) mobileContainer.innerHTML = '';
			return;
		}

		const pillsHtml = buildPillsHtml();
		const clearAllHref = buildClearAllFacetHref(stateParams);

		if (desktopWrapper) {
			desktopWrapper.classList.add('active-facets__wrapper_margin');
			desktopWrapper.innerHTML = `${pillsHtml}
<facet-remove>
	<a href="${clearAllHref}" class="active-facets__button-remove focus-inset button button--simple js-clist-clear-all">
		<span>Clear all</span>
	</a>
</facet-remove>`;
		}

		if (mobileContainer) {
			mobileContainer.innerHTML = `${pillsHtml}
<facet-remove>
	<a href="${clearAllHref}" class="active-facets__button-remove focus-inset button button--simple js-clist-clear-all">
		<span>Clear all</span>
	</a>
</facet-remove>`;
		}

		FacetFiltersForm.toggleActiveFacets(false);
		dispatchSearchClistFilterSync();
	}

	function clistMergeRenderFilters(html, event, origRenderFilters) {
		if (!isCollectionProductsFacetContext()) {
			origRenderFilters(html, event);
			return;
		}

		// /collections/all Products tab and dual-view /search: merge Shopify updates, then restore full FILTER_ORDER list.
		if (isAllPageNativeProductsTab() || isDualViewSearchPage()) {
			applyProductsFacetHtmlMerge(html, event);
			scheduleSyncProductsSidebarFilterOptions(getCurrentFacetSearchParams());
			if (window.ClistFilterOrder?.applyCanonicalGroupLabelsInDom) {
				window.ClistFilterOrder.applyCanonicalGroupLabelsInDom(getProductsFacetsScope());
			}
			if (isDualViewSearchPage()) {
				const sectionId = document.querySelector('#product-grid[data-id]')?.dataset?.id;
				if (sectionId) {
					requestAnimationFrame(() => {
						document.dispatchEvent(
							new CustomEvent('clist:sync-collections-filters-from-products-dom', {
								detail: { sectionId: String(sectionId) },
							})
						);
					});
				}
			}
			return;
		}

		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
		const checkedByParam = captureNativeFacetCheckboxState();
		const facetDetailsElements = parsedHTML.querySelectorAll(
			'#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
		);
		const matchesIndex = (element) => {
			const jsFilter = event ? event.target.closest('.js-filter') : undefined;
			return jsFilter ? element.dataset.filterParam === jsFilter.dataset.filterParam : false;
		};
		const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
		const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

		facetsToRender.forEach((element) => {
			const param = element.dataset.filterParam;
			if (!param) return;
			const isMobile = Boolean(element.closest('#FacetFiltersFormMobile'));
			const scope = isMobile ? '#FacetFiltersFormMobile' : '#FacetFiltersForm';
			const targetFilter =
				document.querySelector(`${scope} .js-filter[data-filter-param="${CSS.escape(param)}"]`) ||
				[...document.querySelectorAll(`${scope} .js-filter`)].find((group) =>
					group.querySelector(`input[type="checkbox"][name="${CSS.escape(param)}"]`)
				);
			if (targetFilter) {
				if (!targetFilter.getAttribute('data-filter-param')) {
					targetFilter.setAttribute('data-filter-param', param);
				}
				mergeNativeFacetFilterGroup(targetFilter, element, checkedByParam);
			}
		});

		FacetFiltersForm.renderAdditionalElements(parsedHTML);
		document.querySelector('facet-filters-form')?.bindFacetTriggers();
		if (countsToRender && event?.target) {
			FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
		}
		scheduleSyncProductsSidebarFilterOptions(getCurrentFacetSearchParams());
	}

	function clearProductCountLoading() {
		document.getElementById('ProductCount')?.classList.remove('loading');
		document.getElementById('ProductCountDesktop')?.classList.remove('loading');
	}

	function clearCollectionFilterLoading() {
		clearProductCountLoading();
		document.getElementById('ProductGridContainer')
			?.querySelector('.collection')
			?.classList.remove('loading');
		const container = getProductGridContainer();
		if (container && typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.endProductGridPending) {
			FacetFiltersForm.endProductGridPending(container);
		}
	}

	function patchFacetFiltersForm() {
		if (patched || !window.customElements) return;
		const FacetCtor = customElements.get('facet-filters-form');
		if (!FacetCtor || FacetCtor.__clistPlpPatched) return;
		FacetCtor.__clistPlpPatched = true;

		const origRenderPage = FacetCtor.renderPage.bind(FacetCtor);
		const origRenderGrid = FacetCtor.renderProductGridContainer.bind(FacetCtor);
		const origRenderCount = FacetCtor.renderProductCount.bind(FacetCtor);
		const origRenderActiveFacets = FacetFiltersForm.renderActiveFacets.bind(FacetFiltersForm);
		const origRenderFilters = FacetFiltersForm.renderFilters.bind(FacetFiltersForm);
		const origRenderSectionFromFetch = FacetFiltersForm.renderSectionFromFetch.bind(FacetFiltersForm);
		const origRenderSectionFromCache = FacetFiltersForm.renderSectionFromCache.bind(FacetFiltersForm);
		const origStaticRenderGrid = FacetFiltersForm.renderProductGridContainer.bind(FacetFiltersForm);
		const origStaticRenderCount = FacetFiltersForm.renderProductCount.bind(FacetFiltersForm);
		const origGetSections = FacetCtor.getSections.bind(FacetCtor);
		const origUpdateURLHash = FacetCtor.updateURLHash.bind(FacetCtor);

		FacetFiltersForm.renderFilters = function clistRenderFilters(html, event) {
			clistMergeRenderFilters(html, event, origRenderFilters);
			FacetFiltersForm.reorderFacetDisplayOrder();
		};

		FacetFiltersForm.renderSectionFromFetch = function clistRenderSectionFromFetch(url, event) {
			if (FacetFiltersForm.isSearchPageContext()) {
				return origRenderSectionFromFetch(url, event);
			}
			const generation = activeFilterGeneration;
			if (!generation) return origRenderSectionFromFetch(url, event);
			if (nativeFetchAbort) nativeFetchAbort.abort();
			nativeFetchAbort = new AbortController();

			fetch(url, { signal: nativeFetchAbort.signal })
				.then((response) => response.text())
				.then((responseText) => {
					if (!isCurrentFilterGeneration(generation)) return;
					const html = responseText;
					FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
					finishCollectionFilterRender(html, event, generation);
				})
				.catch((err) => {
					if (err && err.name === 'AbortError') return;
					if (isCurrentFilterGeneration(generation)) {
						clearCollectionFilterLoading();
						FacetFiltersForm.toggleActiveFacets(false);
					}
					console.error('[clist-products-plp] renderSectionFromFetch failed', err);
				});
		};

		FacetFiltersForm.renderSectionFromCache = function clistRenderSectionFromCache(filterDataUrl, event) {
			if (FacetFiltersForm.isSearchPageContext()) {
				return origRenderSectionFromCache(filterDataUrl, event);
			}
			const generation = activeFilterGeneration;
			if (!generation) return origRenderSectionFromCache(filterDataUrl, event);
			const cachedEntry = FacetFiltersForm.filterData.find(filterDataUrl);
			if (!cachedEntry) {
				return origRenderSectionFromCache(filterDataUrl, event);
			}
			finishCollectionFilterRender(cachedEntry.html, event, generation);
		};

		function renderCollectionProductGrid(html, generation) {
			if (!isCurrentFilterGeneration(generation)) return;

			if (shouldUseListProductsFetchPath()) {
				const parsed = new DOMParser().parseFromString(html, 'text/html');
				const fresh = parsed.getElementById('ProductGridContainer');
				const target = getProductGridContainer();
				if (fresh && target) {
					cleanupOrphanedQuickAddModals();
					target.innerHTML = fresh.innerHTML;
					cleanupOrphanedQuickAddModals({ phase: 'after' });
					const matchedAttr =
						fresh.getAttribute('data-products-count-matched') ||
						fresh.getAttribute('data-filtered-products-count');
					if (matchedAttr != null) setProductsCountMatched(matchedAttr);
					if (typeof colorSwatches === 'function') colorSwatches();
					wireProductsLoadMore();
					document.dispatchEvent(new CustomEvent('clist:sync-products-empty-state'));
					window.dispatchEvent(new Event('resize'));
				}
				return;
			}

			const target = document.getElementById('ProductGridContainer');
			cleanupOrphanedQuickAddModals();
			origStaticRenderGrid(html);
			cleanupOrphanedQuickAddModals({ phase: 'after' });
			applyProductGridTotalFromHtml(html);
			document.dispatchEvent(new CustomEvent('clist:sync-products-empty-state'));
		}

		function finishCollectionFilterRender(html, event, generation) {
			if (!isCurrentFilterGeneration(generation)) return;
			FacetFiltersForm.renderFilters(html, event);
			renderCollectionProductGrid(html, generation);
			if (hasSharedProductsToolbar()) {
				applyAuthoritativeCountFromHtml(html, generation);
			} else if (isCollectionProductsFacetContext()) {
				applyAuthoritativeCountFromHtml(html, generation);
				origStaticRenderCount(html);
			}
			clearCollectionFilterLoading();
			FacetFiltersForm.toggleActiveFacets(false);
			if (!shouldUseListProductsFetchPath()) {
				rebuildNativeActiveFacetsFromForm();
			}
		}

		FacetFiltersForm.renderProductGridContainer = function clistStaticRenderGrid(html) {
			if (FacetFiltersForm.isSearchPageContext()) {
				const result = origStaticRenderGrid(html);
				scheduleSyncProductsSidebarFilterOptions(getCurrentFacetSearchParams());
				return result;
			}
			renderCollectionProductGrid(html, activeFilterGeneration);
		};

		FacetFiltersForm.renderProductCount = function clistStaticRenderCount(html) {
			if (FacetFiltersForm.isSearchPageContext()) {
				clearProductCountLoading();
				return;
			}
			if (hasSharedProductsToolbar()) {
				applyAuthoritativeCountFromHtml(html, activeFilterGeneration);
				clearProductCountLoading();
				return;
			}
			if (isCollectionProductsFacetContext()) {
				applyAuthoritativeCountFromHtml(html, activeFilterGeneration);
				return origStaticRenderCount(html);
			}
			return origStaticRenderCount(html);
		};

		FacetFiltersForm.renderActiveFacets = function clistPlpRenderActiveFacets(html) {
			if (shouldUseListProductsFetchPath()) {
				document.dispatchEvent(new CustomEvent('clist:products-grid-updated', { detail: { html } }));
				return;
			}
			if (isDualViewSearchPage()) {
				rebuildNativeActiveFacetsFromForm();
				return;
			}
			if (FacetFiltersForm.isSearchPageContext()) {
				return origRenderActiveFacets(html);
			}
			if (isCollectionProductsFacetContext()) {
				rebuildNativeActiveFacetsFromForm();
				return;
			}
			return origRenderActiveFacets(html);
		};

		FacetCtor.renderPage = function clistPlpRenderPage(searchParams, event, updateURLHash = true) {
			if (FacetFiltersForm.isSearchPageContext()) {
				return origRenderPage(searchParams, event, updateURLHash);
			}

			const generation = beginFilterRenderRequest();

			if (!shouldUseListProductsFetchPath()) {
				FacetFiltersForm.searchParamsPrev = searchParams;
				syncNativeFacetCheckboxesFromSearchParams();
				origRenderPage(searchParams, event, updateURLHash);
				return;
			}

			FacetFiltersForm.searchParamsPrev = searchParams;
			const url = buildProductsSectionUrl(searchParams);
			const filterDataUrl = (element) => element.url === url;

			const pgc = getProductGridContainer();
			if (pgc) {
				const collection = pgc.querySelector('.collection');
				if (collection) collection.classList.add('loading');
			}

			const finish = (html) => {
				if (!html || !isCurrentFilterGeneration(generation)) return;
				renderCollectionProductGrid(html, generation);
				applyAuthoritativeCountFromHtml(html, generation);
				clearCollectionFilterLoading();
				document.dispatchEvent(
					new CustomEvent('clist:products-grid-updated', {
						detail: { html, generation },
					})
				);
			};

			const cached = FacetFiltersForm.filterData.some(filterDataUrl);
			if (cached) {
				finish(FacetFiltersForm.filterData.find(filterDataUrl).html);
			} else {
				fetchProductsSectionHtml(searchParams, { generation })
					.then(({ html, url: resolvedUrl }) => {
						if (!isCurrentFilterGeneration(generation)) return;
						FacetFiltersForm.filterData = [
							...FacetFiltersForm.filterData,
							{ html, url: resolvedUrl },
						];
						finish(html);
					})
					.catch((err) => {
						if (err && err.name === 'AbortError') return;
						if (isCurrentFilterGeneration(generation)) {
							clearCollectionFilterLoading();
						}
						console.error('[clist-products-plp] fetchProductsSectionHtml failed:', err);
					});
			}

			if (updateURLHash && typeof window.__clistUpdateProductsUrl === 'function') {
				window.__clistUpdateProductsUrl(searchParams);
			}
		};

	FacetCtor.renderProductGridContainer = function clistPlpRenderGrid(html) {
		if (FacetFiltersForm.isSearchPageContext()) {
			const result = origRenderGrid(html);
			scheduleSyncProductsSidebarFilterOptions(getCurrentFacetSearchParams());
			return result;
		}
		renderCollectionProductGrid(html, activeFilterGeneration);
	};

		FacetCtor.renderProductCount = function clistPlpRenderCount(html) {
			if (FacetFiltersForm.isSearchPageContext()) {
				clearProductCountLoading();
				return;
			}
			if (hasSharedProductsToolbar()) {
				applyAuthoritativeCountFromHtml(html, activeFilterGeneration);
				clearProductCountLoading();
				return;
			}
			if (!shouldUseListProductsFetchPath() && isCollectionProductsFacetContext()) {
				applyAuthoritativeCountFromHtml(html, activeFilterGeneration);
				return origRenderCount(html);
			}
			if (shouldUseListProductsFetchPath()) {
				applyAuthoritativeCountFromHtml(html, activeFilterGeneration);
				return;
			}
			return origRenderCount(html);
		};

		FacetCtor.getSections = function clistPlpGetSections() {
			if (!shouldUseListProductsFetchPath()) {
				return origGetSections();
			}
			const grid = getProductsInner()?.querySelector('#product-grid[data-id]');
			if (grid && grid.dataset.id) {
				return [{ section: grid.dataset.id }];
			}
			return origGetSections();
		};

		FacetCtor.updateURLHash = function clistPlpUpdateURLHash(searchParams) {
			if (!shouldUseListProductsFetchPath()) {
				return origUpdateURLHash(searchParams);
			}
			if (typeof window.__clistUpdateProductsUrl === 'function') {
				window.__clistUpdateProductsUrl(searchParams);
			}
		};

		patched = true;
	}

	function tryPatch() {
		patchFacetFiltersForm();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			tryPatch();
			readProductsCountReference();
			renderProductsToolbarCount();
			scheduleSyncProductsSidebarFilterOptions();
		});
	} else {
		tryPatch();
		readProductsCountReference();
		renderProductsToolbarCount();
		scheduleSyncProductsSidebarFilterOptions();
	}
	document.addEventListener('shopify:section:load', tryPatch);

	document.addEventListener('clist:products-grid-updated', (e) => {
		if (e.detail?.html) {
			applyAuthoritativeCountFromHtml(e.detail.html, e.detail.generation);
		}
	});

	window.ClistProductsPlp = {
		patchFacetFiltersForm: tryPatch,
		beginFilterRenderRequest,
		getActiveFilterGeneration,
		isCurrentFilterGeneration,
		wireProductsLoadMore,
		updateToolbarProductCount,
		syncProductsToolbarCountFromDom,
		applyAuthoritativeCountFromHtml,
		renderProductsToolbarCount,
		readProductsCountReference,
		readProductsCountMatched,
		setProductsCountMatched,
		rebuildNativeActiveFacetsFromForm,
		syncNativeFacetCheckboxesFromSearchParams,
		ensureSelectedProductsFiltersVisible,
		syncProductsSidebarFilterOptions,
		scheduleSyncProductsSidebarFilterOptions,
		rebuildProductsSidebarFilterCheckboxes,
		ensureAllProductsFilterOrderValuesVisible,
		enableAllProductsFiltersCheckboxes,
		normalizeFacetParamValue,
		uncheckNativeFacetValue,
		clearNativeFacetCheckboxes,
		isDualViewSearchPage,
		cleanupOrphanedQuickAddModals,
		buildProductsSectionUrl,
		buildProductsSectionUrlForId,
		fetchProductsSectionHtml,
		getProductGridContainer,
	};
})();
